/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum(
  "ChromeActionService",
  ["InitService", "MainContainerService", "SessionService", "NavigationButtonService"],
  /**
   * @param {gbc} context
   * @param {classes} cls
   */
  function (context, cls) {
    /**
     * Service responsible of managing ChromeBar items for
     * genero Actions (in action panel or in back button)
     * Usage : `gbc.ChromeActionService`
     * @namespace gbc.ChromeActionService
     * @gbcService
     */
    class ChromeActionService /** @lends gbc.ChromeActionService */ {
      constructor() {
        /**
         * @type {string}
         */
        this._categoryName = "back";
        /**
         * List all the active chrome action items
         * @type {Array<classes.ItemWidget>}
         */
        this._activeItems = null;
        /**
         * List of current chrome action items that are in the overflow menu (right floating panel)
         * @type {Array<classes.ItemWidget>}
         */
        this._overflowMenuActiveItems = null;
        /**
         * List of current chrome action items visible in the chromeBar
         * @type {Array<classes.ItemWidget>}
         */
        this._chromeBarActiveItems = null;
        /**
         * List of GBC menu items
         * @type {Map<classes.ItemWidget>}
         */
        this._gbcItems = null;
        /**
         * Reference to the button that open the overflow menu
         * @type {classes.ItemWidget}
         */
        this._openOverflowMenuButton = null;
        /**
         * Chromebar resizer listener
         * @type {ResizeObserver}
         */
        this._resizeObserver = null;
        /**
         * Last measured width for chrome action panel
         * @type {Number}
         */
        this._cachedChromeBarWidth = 0;
        /**
         * If set to true, we should force the gbc menu in floating panel and not in chromebar
         * @type {Boolean}
         */
        this._preferOverflowGbcItems = false;
        /**
         * Flag to detect when a chromebar items update is needed (chromebar size change, actions list update)
         */
        this._needRefresh = true;
        /** Scheduler command used to call refresh algorithm */
        this._refreshChromeBarCommand = null;
        /**
         * Animation frame used to execute refresh outside of ResizeObserver listener
         * @type {Function}
         */
        this._refreshAnimationFrame = null;
      }

      // TODO : Find a way not use this service if there is no chromebar
      /**
       * @inheritDoc
       */
      init() {
        this._activeItems = [];
        this._chromeBarActiveItems = [];
        this._overflowMenuActiveItems = [];
        this._gbcItems = new Map();
        this._preferOverflowGbcItems = context.ThemeService.getValue("gbc-ChromeBar-flow-gbc-items");
        this._floatingPanelHiddenHandler = context.MainContainerService.when(context.constants.widgetEvents.floatingPanelHidden, () => {
          this._openOverflowMenuButton?.setAriaAttribute("expanded", false);
        });
      }

      /**
       * @inheritDoc
       */
      destroy() {
        if (this._floatingPanelHiddenHandler) {
          this._floatingPanelHiddenHandler();
          this._floatingPanelHiddenHandler = null;
        }
        if (this._overflowMenuButton) {
          this._overflowMenuButton.destroy();
          this._overflowMenuButton = null;
        }
        this._chromeBarActiveItems.length = 0;
        this._overflowMenuActiveItems.length = 0;
        this._activeItems.length = 0;
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
        this._destroyGbcItems();
      }

      /**
       * Destroy gbc menu items of the chromebar
       * @private
       */
      _destroyGbcItems() {
        if (this._gbcItems) {
          for (const widget of this._gbcItems.values()) {
            widget.destroy();
          }
          this._gbcItems.clear();
        }
      }

      /**
       * Set back action as sidebar button action
       * @param {classes.WidgetBase} actionWidget
       * @param {Function} clickAction
       */
      addBackAction(actionWidget, clickAction) {
        context.NavigationButtonService.addAction(this._categoryName, actionWidget.getUniqueIdentifier(), clickAction);
      }

      /**
       * Remove back action from sidebar button action
       * @param {classes.WidgetBase} actionWidget
       */
      removeBackAction(actionWidget) {
        context.NavigationButtonService.removeAction(this._categoryName, actionWidget.getUniqueIdentifier());
      }

      /**
       * Add chrome action item in proper section (chromebar or right panel)
       * @param {classes.ItemWidget} actionWidget
       */
      addChromeAction(actionWidget) {
        // Don't add twice an action, and also ignore close action as it's handled by the GBC in a singleton pattern
        if (this._activeItems.includes(actionWidget) || actionWidget.getAuiName() === "close") {
          return;
        }
        this._activeItems.push(actionWidget);
        actionWidget.addClasses(context.constants.widgetCssClasses.chromeItem);
        actionWidget.when(context.constants.widgetEvents.visibilityChange, this._setChromeBarDirty.bind(this));
        this._setChromeBarDirty();
      }

      /**
       * Remove chrome action item from chromebar or right panel
       * @param {classes.WidgetBase} actionWidget
       */
      removeChromeAction(actionWidget) {
        if (!this._activeItems.includes(actionWidget)) {
          return;
        }
        cls.ArrayHelper.remove(this._activeItems, actionWidget);
        cls.ArrayHelper.remove(this._overflowMenuActiveItems, actionWidget);
        actionWidget.getElement().remove();
        this._setChromeBarDirty();
      }

      /**
       * Open right floating panel with its action items.
       * The overflow popup is a real WAI-ARIA menu, so items get role="menuitem" while inside it,
       * and each is wrapped in an <li role="none"> to satisfy the <menu> content model.
       */
      openRightFloatingPanel() {
        const menu = document.createElement("menu");
        menu.setAttribute("role", "menu");
        for (const widget of this._overflowMenuActiveItems) {
          widget.setAriaRole("menuitem");
          const li = document.createElement("li");
          li.setAttribute("role", "none");
          li.append(widget.getElement());
          menu.append(li);
        }
        context.MainContainerService.setFloatingPanelContent(menu);
        context.MainContainerService.showFloatingPanel("right");
        this._openOverflowMenuButton?.setAriaAttribute("expanded", true);
      }

      /**
       * Set Default TTF color
       * @param {string} color - rgb formatted or css name
       */
      setDefaultTTFColor(color) {
        if (color === this._defaultTTFColor) {
          return;
        }
        context.MainContainerService.getChromeBar()?.style.setProperty("--zmdiTTFColor", color);
        this._defaultTTFColor = color;

        for (const element of this._activeItems) {
          const item = element;
          const fnDefaultTTF = item.setDefaultColor || item.setDefaultTTFColor;
          fnDefaultTTF.bind(item)(color);
        }
      }

      /**
       * On each chromebar action panel resize, recalculate available size to split action widgets between chrome action panel and dropdown
       * @private
       */
      _listenChromeActionBarSizeChange() {
        if (!context.MainContainerService.isChromeBarVisible()) {
          return;
        }
        // jshint -W117
        this._resizeObserver = new ResizeObserver((entries) => {
          if (entries[0] && entries[0].contentBoxSize) {
            const newSize = Math.floor(entries[0].contentBoxSize[0].inlineSize);
            // reduce number of refresh by refreshing each time size increased or decreased of at least 10 pixels
            if (Math.abs(this._cachedChromeBarWidth - newSize) > 10) {
              this._cachedChromeBarWidth = newSize;
              this._needRefresh = true;
              // Added a requestAnimationFrame to avoid infinite loop of ResizeObserver when updating dom size inside ResizeObserver event
              if (this._refreshAnimationFrame) {
                window.cancelAnimationFrame(this._refreshAnimationFrame);
              }
              this._refreshAnimationFrame = window.requestAnimationFrame(this.refresh.bind(this));
            }
          }
        });
        this._resizeObserver.observe(context.MainContainerService.getChromeBarEndContainer());
      }

      /**
       * Recalculate and allocate action items in proper chrome action sections.
       * Uses a single-pass width budget algorithm: items are measured once in the DOM,
       * filled in order until the available space is exhausted, then the rest go to the overflow menu.
       */
      refresh() {
        this._refreshChromeBarCommand = null;
        if (!context.MainContainerService.isChromeBarVisible()) {
          return;
        }

        if (!this._resizeObserver) {
          this._listenChromeActionBarSizeChange();
        }

        if (!this._needRefresh) {
          return;
        }

        if (!context.MainContainerService.isDockedPanelVisible()) {
          context.MainContainerService.hideFloatingPanel();
        }

        if (this._cachedChromeBarWidth === 0) {
          this._cachedChromeBarWidth = context.MainContainerService.getChromeBarEndContainer().clientWidth;
        }

        this._chromeBarActiveItems.length = 0;
        this._overflowMenuActiveItems.length = 0;
        this._activeItems = this._activeItems.filter((widget) => !widget.isDestroyed() && widget.getElement());
        const allItems = this._buildItemListForRefresh();

        // Items are now in the toolbar (role="toolbar") — clear any role="menuitem" left over from
        // a previous overflow visit so screen readers don't announce buttons as menuitems outside a menu.
        for (const widget of allItems) {
          widget.setAriaRole(null);
        }

        context.MainContainerService.setChromeBarEndContent(...allItems.map((widget) => widget.getElement()));

        // Show the overflow button before measuring so its offsetWidth is available
        this._openOverflowMenuButton.setHidden(false);

        const availableWidth = context.MainContainerService.getChromeBarEndContainer().clientWidth;
        const overflowButton = this._openOverflowMenuButton;
        const cantOverflowItems = allItems.filter((item) => item.cantOverflow && item !== overflowButton);
        const overflowableItems = allItems.filter((item) => !item.cantOverflow);

        const cantOverflowWidth = cantOverflowItems.reduce((sum, item) => sum + item.getElement().offsetWidth, 0);
        const overflowButtonWidth = overflowButton.getElement().offsetWidth;
        const totalOverflowableWidth = overflowableItems.reduce((sum, item) => sum + item.getElement().offsetWidth, 0);

        if (cantOverflowWidth + totalOverflowableWidth <= availableWidth) {
          // Everything fits, hide the button unless items were pre-placed in overflow (e.g. _preferOverflowGbcItems)
          this._openOverflowMenuButton.setHidden(this._overflowMenuActiveItems.length === 0);
          this._needRefresh = false;
          return;
        }

        // Fill items in order until the first one doesn't fit, then overflow the rest
        const spaceForOverflowable = availableWidth - cantOverflowWidth - overflowButtonWidth;
        let accumulatedWidth = 0;
        let overflowStarted = false;
        for (const item of overflowableItems) {
          if (!item.isVisible()) {
            continue;
          }
          const itemWidth = item.getElement().offsetWidth;
          if (!overflowStarted && accumulatedWidth + itemWidth <= spaceForOverflowable) {
            accumulatedWidth += itemWidth;
            this._chromeBarActiveItems.push(item);
          } else {
            overflowStarted = true;
            item.getElement().remove();
            this._overflowMenuActiveItems.push(item);
          }
        }
        this._needRefresh = false;
      }

      /**
       * Create an array containing all the actions displayed in the chromebar
       * that are eligible for the refresh algorythm
       * Items that are "prefered overflow" are directly placed in the overflow menu
       * @returns {classes.ItemWidget[]}
       */
      _buildItemListForRefresh() {
        // retrieve all items to display in chrome action pan
        // el : actions + gbc menu items
        // sort actions by ascending aui number (order of creation)
        const gbcItems = Array.from(this._gbcItems.values());
        const allItems = [...this._activeItems.sort((node1, node2) => node1.getAuiLinkedUniqueIdentifier() - node2.getAuiLinkedUniqueIdentifier())];
        // If we should force overflow ...
        if (this._preferOverflowGbcItems) {
          // .. for each gbc item, test if it can overflow or not,
          // and take actions
          for (const gbcItem of gbcItems) {
            if (gbcItem.cantOverflow) {
              allItems.push(gbcItem);
            } else {
              this._overflowMenuActiveItems.push(gbcItem);
            }
          }
        } else {
          allItems.push(...gbcItems);
        }
        // Add the open overflow menu button any way, but hide it
        allItems.push(this._openOverflowMenuButton);

        // Ensure close button is always last item (even after overflow button)
        if (gbc.ThemeService.getValue("gbc-ChromeBar-pin-close-button")) {
          return allItems.sort((itemA, itemB) => {
            return (itemA.getAuiName() === "close") - (itemB.getAuiName() === "close");
          });
        }
        return allItems;
      }

      /**
       * To be called when adding/removing actions
       * It will trigger a logic to refresh the chromebar layout
       * once the scheduler process is done
       */
      _setChromeBarDirty() {
        this._needRefresh = true;
        const app = context.SessionService.getCurrent()?.getCurrentApplication();
        if (app && !this._refreshChromeBarCommand) {
          this._refreshChromeBarCommand = app.scheduler.schedulerProcessEndedEvent(this.refresh.bind(this), true);
        }
      }

      /**
       * Create or update all the GBC menu item (about, settings and so...)
       */
      createUpdateGbcMenu() {
        this._setChromeBarDirty();
        this._destroyGbcItems();

        const gbcItems = context.ThemeService.getValue("gbc-ChromeBar-visible-items").split(" ");
        if (context.DebugService.isActive()) {
          gbcItems.push("debug");
        }
        gbcItems.unshift("filter");
        gbcItems.push("close"); // Close button should not be removed !
        const uniqueArray = [...new Set(gbcItems)]; // remove potential duplicates

        // Create all the gbc items
        // Items are <button> living inside the ChromeBar EndContainer (role="toolbar"). They only
        // get role="menuitem" when moved into the overflow <menu role="menu"> by openRightFloatingPanel.
        for (const widgetName of uniqueArray) {
          // Use the factory
          if (cls.GbcItemWidgetFactory.gbcItemDefinitionList.includes(widgetName)) {
            // if specific chromebar item exists, instanciate it
            const gbcWidget = cls.GbcItemWidgetFactory[widgetName]({ appHash: gbc.systemAppId });
            gbcWidget.addClasses(context.constants.widgetCssClasses.chromeItem);
            this._gbcItems.set(widgetName, gbcWidget);
          }
        }
        if (!this._openOverflowMenuButton) {
          this._openOverflowMenuButton = cls.GbcItemWidgetFactory.openOverflowMenu({ appHash: gbc.systemAppId });
          this._openOverflowMenuButton.addClasses(context.constants.widgetCssClasses.chromeItem);
          // The controlled element is the FloatingPanel <div role="dialog"> — the inner content
          // (built in openRightFloatingPanel) is a <menu role="menu">, but aria-haspopup must match
          // the role of the element pointed by aria-controls, not the inner content.
          this._openOverflowMenuButton.setAriaAttribute("haspopup", "dialog");
          this._openOverflowMenuButton.setAriaAttribute("controls", context.MainContainerService.getFloatingPanel()?.id);
          this._openOverflowMenuButton.setAriaAttribute("expanded", false);
        }
      }

      /**
       * Return GBC menu widget
       * @param actionName
       * @return {classes.ItemWidget}
       */
      getGbcMenuItem(actionName) {
        if (actionName === "openOverflowMenu") {
          return this._openOverflowMenuButton;
        }
        return this._gbcItems.get(actionName);
      }
    }
    gbc.ChromeActionService = new ChromeActionService();
    context.InitService.register(context.ChromeActionService);
  }
);
